<template>
    <div>
        <loading-progress-bar v-if="loading" :loading="loading" color="secondary"></loading-progress-bar>
        <div v-if="!loading&&objectUrn" :style="{height:height+'px'}">
            <forge-vuer
                :getAccessToken="getToken"
                ref="forgevuer"
                @geometry-loaded-event="handleGeometryLoaded"
                @model-root-loaded-event="initialSetup"
                :urn="objectUrn"/>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import LoadingProgressBar from './../LoadingProgressBar'
import ForgeVuer from 'forge-vuer';
import {callAPI} from "@/helper";
export default {
    props: {
        dateRange: {type: Object, default: null},
        url: {type: String, default: null},
        height: {type: Number, default: 800},
        surfaceId: {type: Number, default: null},
        floorName: {type: String, default: "Main"},
        points: {
            type: Array, default: () => {
                return []
            }
        },
        agg: {type: String, default: "D"},
        aggOpp: {type: String, default: "mean"},
        onOnly: {type: Boolean, default: false},
        whiteMode:{type:Boolean, default: false},

    },
    components: {LoadingProgressBar, ForgeVuer},
    computed: {},
    name: 'ForgeViewerHeatmapWidget',
    watch: {
        dateRange(){
            this.reloadData()
        }
    },
    data: function () {
        return {
            loading: true,
            timeout: null,
            objectUrn: null,
            vizExtn: null,
            minValue:0,
            maxValue:1,
            heatmapData: {},
        }
    },
    mounted() {
        this.refresh()
    },
    methods: {
        debounce(callback, wait) {
            clearTimeout(this.timeout);
            this.timeout = setTimeout(() => callback(), wait);
        },
        refresh() {
            this.debounce(this.reloadViewer, 200);
        },
        clamp(num, min, max) {
            return Math.min(Math.max(num, min), max)
        },
        initialSetup() {
            if (this.whiteMode){
                this.$refs.forgevuer.viewerService.Viewer3D.setBackgroundColor(255,255,255,255,255,255)
            } else {
                this.$refs.forgevuer.viewerService.Viewer3D.setBackgroundColor(66,66,66,66,66,66)
            }
        },
        getSensorValue(device, sensorType) {
            let sensorValue = (this.heatmapData[device.id] - this.minValue) / (this.maxValue - this.minValue);
            return this.clamp(sensorValue, 0.0, 1.0);
        },
        async reloadData() {
            let me = this
            if (this.points && this.points.length > 0&&this.vizExtn) {
                let pipeline = me.onOnly ? [{
                    "type": "filter",
                    "filter_op": "eq",
                    "filter_by": "* - on",
                    "filter_value": 1,
                }, {type: "agg", agg: me.agg, agg_op: me.aggOpp}] : [{type: "agg", agg: me.agg, agg_op: me.aggOpp}]
                let promises = me.points.map((point) => {
                    return callAPI('data3/', {
                        date_range: [this.dateRange.start, this.dateRange.end],
                        filters: {
                            equipment_name: point.equipment_name,
                            sensor_name: point.name,
                            facility_name: point.facility_name ? point.facility_name : me.$store.state.pointsListStore.pointsList.facility_name
                        },
                        pipeline: pipeline
                    })
                })
                let results = await Promise.all(promises)
                let heatmapData = {}
                for (let index in me.points) {
                    let point = me.points[index]
                    heatmapData[point.equipment_name + " - " + point.name] = results[index].data[point.equipment_name + " - " + point.name] ? results[index].data[point.equipment_name + " - " + point.name].reduce((acc, curr) => curr ? curr : acc) : null
                }
                me.heatmapData = heatmapData
                me.minValue = Math.min(...Object.values(heatmapData))
                me.maxValue = Math.max(...Object.values(heatmapData))
                me.vizExtn.renderSurfaceShading(me.floorName, "temperature", me.getSensorValue);
            }
        },
        async handleGeometryLoaded(model) {
            let me = this
            if (this.points && this.points.length > 0) {
                let pipeline = me.onOnly ? [{
                    "type": "filter",
                    "filter_op": "eq",
                    "filter_by": "* - on",
                    "filter_value": 1,
                }, {type: "agg", agg: me.agg, agg_op: me.aggOpp}] : [{type: "agg", agg: me.agg, agg_op: me.aggOpp}]
                let promises = me.points.map((point) => {
                    return callAPI('data3/', {
                        date_range: [this.dateRange.start, this.dateRange.end],
                        filters: {
                            equipment_name: point.equipment_name,
                            sensor_name: point.name,
                            facility_name: point.facility_name ? point.facility_name : me.$store.state.pointsListStore.pointsList.facility_name
                        },
                        pipeline: pipeline
                    })
                })
                let results = await Promise.all(promises)
                let heatmapData = {}
                let sensors = []
                for (let index in me.points) {
                    let point = me.points[index]
                    sensors.push({
                        id: point.equipment_name + " - " + point.name,
                        position: {x: point.x, y: point.y, z: point.z},
                        type: "temperature",
                        sensorTypes: ["temperature"],
                    })
                    heatmapData[point.equipment_name + " - " + point.name] = results[index].data[point.equipment_name + " - " + point.name] ? results[index].data[point.equipment_name + " - " + point.name].reduce((acc, curr) => curr ? curr : acc) : null
                }
                me.heatmapData=heatmapData
                me.minValue=Math.min(...Object.values(heatmapData))
                me.maxValue=Math.max(...Object.values(heatmapData))
                await this.$refs.forgevuer.viewerService.Viewer3D.loadExtension("Autodesk.DataVisualization");
                const dataVizExtn = this.$refs.forgevuer.viewerService.Viewer3D.getExtension("Autodesk.DataVisualization");
                const DataVizCore = Autodesk.DataVisualization.Core
                const structureInfo = new DataVizCore.ModelStructureInfo(model.model);
                if(this.surfaceId){
                    let levelRoomsMap = new DataVizCore.LevelRoomsMap();
                    let space = new DataVizCore.Room(
                        this.surfaceId, //Room's DbId
                        "Space",
                        new THREE.Box3(new THREE.Vector3(0, 0, 0), new THREE.Vector3(10, 10, 10))
                    );
                    for (let sensor of sensors) {
                        space.addDevice(sensor)
                    }
                    levelRoomsMap.addRoomToLevel(me.floorName, space);

                    let shadingData = await structureInfo.generateSurfaceShadingData(sensors, levelRoomsMap);
                    await dataVizExtn.setupSurfaceShading(model.model, shadingData);
                } else {
                    let shadingData = await structureInfo.generateSurfaceShadingData(sensors);
                    await dataVizExtn.setupSurfaceShading(model.model, shadingData);
                }
                dataVizExtn.renderSurfaceShading(me.floorName, "temperature", me.getSensorValue);
                me.vizExtn=dataVizExtn
            }
        },
        async getToken(onSuccess) {
            axios.post(this.url.replace('public', 'sign').replace('mode=embed', 'oauth2=true'), {}, {overrideCarnotAuth: true})
                .then(response => {
                    onSuccess(response.data.accessToken, response.data.validitySeconds);
                })
                .catch(error => {
                    console.log(error);
                });
        },
        async reloadViewer() {
            if (this.url && this.url !== "") {
                let me = this
                me.loading = true
                let urnResponse = await axios.get(this.url.replace('public', 'metadata').replace('mode=embed', ''), {overrideCarnotAuth: true})
                me.objectUrn = urnResponse.data.success.body.viewing.views.viewableUrn
                me.loading = false
            }
        }
    },
}
</script>